import { Card } from 'primereact/card';
import { FloatLabel } from 'primereact/floatlabel';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { EditToken, Token } from '../components/EditToken';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { PartnerService, PartnerWithTransactions } from '../services/PartnerService';
import PartnerPage from './PartnerPage';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import Fragment from '../components/Fragment';

const NewToken: React.FC = () => {
    const isMdUp = useMediaQuery('(min-width: 1124px)');
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { id } = useParams<{ id: string }>();
    const [token, setToken] = useState<Token | null>(null);
    const [done, setDone] = useState(false);
    const [partner, setPartner] = useState<PartnerWithTransactions | null>(null);
    const [copyLink, setCopyLink] = useState<string | null>(null);
    const toast = useRef<Toast>(null);

    const lookupPartner = async (id: string) => {
        try {
            setDone(false);
            setPartner(null);
            setCopyLink(null);
            const partner = await PartnerService.getPartnerById(id);
            setPartner(partner);
            if (partner.numberOfTokens > 0) {
                setToken({ partnerName: partner.name, company: '', occupation: '', dueDate: undefined, sendWelcomeEmail: true } as Token);
            } else {
                setToken(null);
            }
        } catch (error: any) {
            navigate('/login');
        }
    };

    useEffect(() => {
        if (!id) {
            navigate('/login');
        }
        lookupPartner(id!);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, navigate]);

    const handleSave = async (token: Token) => {
        try {
            const savedToken = await PartnerService.createTokenForPartner(id!, token);
            setCopyLink(`${window.location.origin}/questionare/${savedToken.id}`);
            setDone(true);
            setToken(savedToken);
            toast.current?.show({ severity: 'success', summary: t('newToken.success'), life: 3000 });
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: t('editToken.saveError'), detail: "" + error, life: 3000 });
        }
    };

    const handleCopy = (success: boolean) => {
        if (success) {
            toast.current?.show({ severity: 'success', summary: t('editToken.copySuccess'), life: 1000 });
        } else {
            toast.current?.show({ severity: 'error', summary: t('editToken.copyError'), life: 1000 });
        }
    };

    return !partner ? (
        <div className="flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 200px)' }}>
            <ProgressSpinner />
        </div>
    ) : (
        <>
            <Toast ref={toast} />
            <PartnerPage title={partner ? t('partnerPortal') + " : " + partner.name + " (" + t('newToken.licenses') + " " + (partner.numberOfTokens ?? 0) + ")" : 'Loading...'} />
            <div className="grid mt-8" style={{ padding: '0 2rem' }}>
                <div className={isMdUp ? "col-10 mt-6" : "col-12 mt-6"}>
                <Card>
                        <div className="grid" style={{ padding: '0 2rem' }}>
                            {!done && token ?
                                <>
                                    <Fragment text={partner.fragments["partner-top"]} />
                                    <div className="field  col-12">
                                        <EditToken token={token} onSave={handleSave} unauthenticated={true} />
                                    </div>
                                </>
                                :
                                copyLink ? (
                                    <>
                                        <Fragment text={partner.fragments["partner-thankyou"]} replacements={token||{}} />
                                        <div className="field col-12" style={{ marginTop: '1rem' }}>
                                            <FloatLabel>
                                                <label htmlFor="copyLink">{t('editToken.copyLink')}</label>
                                                <IconField>

                                                    <InputText id="copyLink" style={{ width: "100%" }} value={copyLink} readOnly />
                                                    <CopyToClipboard text={copyLink} onCopy={(text, result) => handleCopy(result)}>
                                                        <InputIcon className="pi pi-copy" type="button" />
                                                    </CopyToClipboard>
                                                </IconField>
                                            </FloatLabel>
                                            <div className="field col-12">
                                                <Button onClick={() => lookupPartner(id!)}>
                                                    {t('newToken.newToken')}
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                ) :
                                    <div className="field col-12">
                                        <h2>{t('newToken.noTokens')}</h2>
                                    </div>}
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default NewToken;
